<script setup lang="ts">
import LawyerCardFeedback from '@/widgets/LawyerCard/ui/LawyerCardFeedback.vue';
import RatingTooltip from '@/widgets/LawyerCard/ui/Tooltips/RatingTooltip.vue';
import type {
  ICardRatingInfoProps,
  ICardRatingInfoEmits,
} from '@/widgets/LawyerCard/model/types/lawyerCard';

const props = withDefaults(defineProps<ICardRatingInfoProps>(), {
  urlAvatar: '',
  urlAvatarThumbnail: '',
  urlAvatarThumbnail2x: '',
  feedbackRating: 0,
  isDetailpage: false,
  isPriorityImage: false,
  isPushToFeedbacks: false,
  isLawyerCompanyCard: false,
});

const imageLoading: ComputedRef<'eager' | 'lazy'> = computed(() =>
  props.isPriorityImage ? 'eager' : 'lazy',
);
const imageFetchPriority: ComputedRef<'high' | 'auto'> = computed(() =>
  props.isPriorityImage ? 'high' : 'auto',
);

const imageSrc: ComputedRef<string> = computed(
  () => props.urlAvatarThumbnail || props.urlAvatar || '',
);
const imageSrcset: ComputedRef<string | undefined> = computed(() =>
  props.urlAvatarThumbnail && props.urlAvatarThumbnail2x
    ? `${props.urlAvatarThumbnail} 1x, ${props.urlAvatarThumbnail2x} 2x`
    : undefined,
);

const emit = defineEmits<ICardRatingInfoEmits>();

const isMobile: boolean = useIsMobile();

const isShowTooltip: Ref<boolean> = ref(false);
const isShowRating: ComputedRef<boolean> = computed(() => props.feedbackCount > 0);

const showTooltip = (): void => {
  isShowTooltip.value = true;
};

const hideTooltip = (): void => {
  isShowTooltip.value = false;
};

const showImage = (): void => {
  emit('show-image');
};
</script>

<template>
  <div :class="['card-rating-info', { _mobile: isMobile }]">
    <img
      :loading="imageLoading"
      :fetchpriority="imageFetchPriority"
      :src="imageSrc"
      :srcset="imageSrcset"
      :class="['card-rating-info__image', { '_zoom-in': isDetailpage }]"
      :alt="imageAlt"
      width="122"
      height="122"
      data-testid="card-rating-info-image"
      @click.prevent="showImage"
    />
    <div class="card-rating-info__feedback-wrapper">
      <LawyerCardFeedback
        :is-push-to-feedbacks="isPushToFeedbacks"
        :is-lawyer-company-card="isLawyerCompanyCard"
      />
    </div>
    <div
      v-if="isShowRating"
      class="card-rating-info__rating-wrapper"
      @mouseover="showTooltip"
      @mouseleave="hideTooltip"
    >
      <span class="card-rating-info__rating-count">
        {{ feedbackRating.toFixed(1) }}
      </span>
      <RatingTooltip v-model="isShowTooltip" />
    </div>
  </div>
</template>

<style lang="less">
.card-rating-info {
  display: inline-flex;
  flex-direction: column;
  position: relative;

  &__image {
    width: 122px;
    height: 122px;
    margin-bottom: 10px;
    border-radius: @main-size;
  }

  &__image._zoom-in {
    cursor: zoom-in;
  }

  &__rating-wrapper {
    position: absolute;
    top: 0;
    right: -@size-x3;
    width: @size-x6;
    height: @size-x6;
  }

  &__rating-count {
    width: 100%;
    height: 100%;
    background-color: @color-persimmon;
    border-radius: 100%;
    color: white;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: -0.3px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  &._mobile &__feedback-wrapper {
    max-width: 86px;
  }

  &._mobile &__image {
    width: 86px;
    height: 86px;
    margin-bottom: 6px;
  }
}
</style>
